// Packages
import { useState, useEffect, useRef, type ChangeEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  SiteHeader,
  Banner,
  IconButton,
  useKeyboardShortcuts,
} from "@jkast90/kast-common/ui";

// Contexts
import { useSearch } from "../../hooks/useReduxSearch";
import { useReduxReminder } from "../../hooks/useReduxReminder";
import { AccountControls } from "../../accountKit";

// Components
import { Icon, ReminderDrawer, ReminderPopup } from "../ui";

// Utils
import { bannerStore } from "../../utils/bannerStore";
import { readJson } from "@jkast90/kast-common/storage";
import { toDate } from "../ui/reminderShape";
import type { Reminder } from "../../store/slices/reminderSlice";

// Styling
import styles from "./NavBar.module.css";
import bannerStyles from "../../styles/Banners.module.css";

export default function NavBar({ onHelpClick }: { onHelpClick: () => void }) {
  // The bar is fixed and wraps on a narrow screen (three rows at 390px), so the
  // page below it takes its measured height, not a fixed 60px.
  const navRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const nav = navRef.current;
    if (!nav || typeof ResizeObserver === "undefined") return;
    const root = document.documentElement;
    const observer = new ResizeObserver(() => {
      root.style.setProperty("--kui-nav-height", `${nav.offsetHeight}px`);
    });
    observer.observe(nav);
    return () => {
      observer.disconnect();
      root.style.removeProperty("--kui-nav-height");
    };
  }, []);
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [showReminders, setShowReminders] = useState(false);
  const [popupReminder, setPopupReminder] = useState<Reminder | null>(null);
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const { searchTerm, setSearchTerm } = useSearch();
  const {
    reminders,
    addReminder,
    snoozeReminder,
    markCompleted,
    removeReminder,
    setActivePopupId,
  } = useReduxReminder();

  // Load from localStorage once, on mount, if reminders are empty.
  const remindersRestoredRef = useRef(false);
  useEffect(() => {
    if (remindersRestoredRef.current) return;
    remindersRestoredRef.current = true;
    if (reminders.length > 0) return;
    const storedReminders = readJson<Reminder[]>("note_reminders", [], {
      isValid: (v): v is Reminder[] => Array.isArray(v),
    });
    if (storedReminders.length > 0) {
      storedReminders.forEach((reminder) => addReminder(reminder));
    }
  }, [reminders.length, addReminder]);

  // setActivePopupId is a new function each render; the reminder check
  // below reads the latest through a ref so its interval isn't reset.
  const setActivePopupIdRef = useRef(setActivePopupId);
  useEffect(() => {
    setActivePopupIdRef.current = setActivePopupId;
  });

  // Show reminder popup if any are overdue
  useEffect(() => {
    const checkReminders = () => {
      const now = Date.now();
      const next = reminders.find(
        (r) => !r.completed && toDate(r.due).getTime() <= now,
      );
      if (next) {
        setPopupReminder(next);
        setActivePopupIdRef.current?.(next.id);
      }
    };
    checkReminders();
    const interval = setInterval(checkReminders, 5000);
    return () => clearInterval(interval);
  }, [reminders]);

  const handleSearch = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (timeoutRef.current !== undefined) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => setSearchTerm(value), 100);
  };

  // Keyboard shortcuts (Shift+F for search, Shift+N/L/B for navigation)
  useKeyboardShortcuts({
    "shift+f": () => {
      if (!searchInputRef.current) return;
      searchInputRef.current.focus();
      searchInputRef.current.select(); // Select any existing text
    },
    "shift+n": () => navigate("/notes"),
    "shift+l": () => navigate("/links"),
    "shift+b": () => navigate("/browse"),
  });

  const links = [
    { href: "/links", label: "Links" },
    { href: "/notes", label: "Notes" },
    { href: "/workspace", label: "Workspace" },
    { href: "/browse", label: "Browse" },
    { href: "/reference", label: "Reference" },
  ];

  return (
    <div ref={navRef} className={styles.navbar}>
      <SiteHeader
        className={styles.header}
        brand={{ logo: "/tapestry.png", alt: "Logo", href: "/about" }}
        onHelp={onHelpClick}
        links={links}
        stackBelow={768}
        activeHref={pathname}
        account={<AccountControls />}
      >
        <input
          ref={searchInputRef}
          type="text"
          placeholder="Search..."
          defaultValue={searchTerm}
          onChange={handleSearch}
          className={styles.searchBox}
        />
        <IconButton
          className={styles.toggle}
          onClick={() => setShowReminders((prev) => !prev)}
          label="Reminders"
        >
          <Icon name="alarm" />
        </IconButton>
      </SiteHeader>
      <Banner
        store={bannerStore}
        className={bannerStyles.nav}
        dismissLabel="Close banner"
      />

      {showReminders && (
        <ReminderDrawer
          reminders={reminders}
          onSnooze={(r, minutes) => snoozeReminder(r.id, minutes)}
          onComplete={(r) => markCompleted(r.id)}
        />
      )}

      {popupReminder && (
        <ReminderPopup
          reminder={popupReminder}
          onComplete={() => {
            markCompleted(popupReminder.id);
            setPopupReminder(null);
          }}
          onSnooze={(minutes) => {
            snoozeReminder(popupReminder.id, minutes);
            setPopupReminder(null);
          }}
          onClear={() => {
            removeReminder(popupReminder.id);
            setPopupReminder(null);
          }}
        />
      )}
    </div>
  );
}
